import { memo } from 'react'

import { cva } from 'class-variance-authority'
import { twMerge } from 'tailwind-merge'

import Icon from '../Icon/Icon'
import { IconKey } from '../Icon/util'
import { T } from '../Text/Text'

const classes = cva(
  [
    'relative',
    'focus-visible:outline-2',
    'outline-offset-1',
    'outline-outline',
    'border',
    'text-center',
    'flex',
    'items-center',
    'justify-center',
    'gap-2',
    'nowrap',
    'transition-all',
    'duration-100',
    'after:content-[""]',
    'after:absolute',
    'after:left-[-1px]',
    'after:top-[-1px]',
    'after:w-[calc(100%_+_2px)]',
    'after:h-[calc(100%_+_2px)]',
    'after:pointer-events-none',
    'after:transition-all',
    'after:duration-50',
  ],
  {
    variants: {
      color: {
        default: [],
        danger: [],
      },
      iconOnly: {
        false: [],
        true: ['leading-4'],
      },
      intent: {
        primary: [],
        default: [],
        text: ['bg-transparent', 'border-transparent'],
      },
      isDisabled: {
        false: ['cursor-pointer'],
        true: ['cursor-not-allowed'],
      },
      size: {
        sm: ['h-[24px]', 'rounded-[4px]', 'after:rounded-[4px]'],
        md: ['h-[32px]', 'rounded-[6px]', 'after:rounded-[6px]'],
        lg: ['h-[40px]', 'rounded-[8px]', 'after:rounded-[8px]'],
      },
    },
    compoundVariants: [
      {
        iconOnly: true,
        size: 'sm',
        class: 'w-[24px]',
      },
      {
        iconOnly: false,
        size: 'sm',
        class: 'px-[7px]',
      },
      {
        iconOnly: true,
        size: 'md',
        class: 'w-[32px]',
      },
      {
        iconOnly: false,
        size: 'md',
        class: 'px-[15px]',
      },
      {
        iconOnly: true,
        size: 'lg',
        class: 'w-[40px]',
      },
      {
        iconOnly: false,
        size: 'lg',
        class: 'px-[15px]',
      },
      {
        color: 'default',
        intent: 'primary',
        isDisabled: false,
        class: [
          'bg-primary',
          'border-primary',
          'text-white',
          'hover:bg-primary-hover',
          'hover:border-primary-hover',
          'shadow-[0_2px_0_rgba(0,0,0,0.05)]',
        ],
      },
      {
        color: 'default',
        intent: 'primary',
        isDisabled: true,
        class: ['bg-neutral-100', 'border-neutral-300', 'text-neutral-400'],
      },
      {
        color: 'danger',
        intent: 'primary',
        isDisabled: false,
        class: [
          'bg-red-600',
          'border-red-600',
          'text-white',
          'hover:bg-red-500',
          'after:inset-shadow-[0_-1px_0_0_rgba(0,0,0,0.25)]',
          'active:after:inset-shadow-[0_2px_0_0_rgba(0,0,0,0.25)]',
        ],
      },
      {
        color: 'danger',
        intent: 'primary',
        isDisabled: true,
        class: ['bg-red-50', 'border-red-300', 'text-red-400'],
      },
      {
        color: 'default',
        intent: 'default',
        isDisabled: false,
        class: [
          'bg-white',
          'border-neutral-200',
          'text-neutral-950',
          'hover:text-primary-hover',
          'hover:border-primary-hover',
          'shadow-[0_2px_0_rgba(0,0,0,0.02)]',
        ],
      },
      {
        color: 'default',
        intent: 'default',
        isDisabled: true,
        class: ['bg-white', 'border-neutral-300', 'text-neutral-300'],
      },
      {
        color: 'danger',
        intent: 'default',
        isDisabled: false,
        class: [
          'bg-white',
          'border-red-600',
          'text-red-600',
          'hover:bg-red-50',
          'active:after:inset-shadow-[0_2px_0_0_rgba(231,0,11,0.25)]',
        ],
      },
      {
        color: 'danger',
        intent: 'default',
        isDisabled: true,
        class: ['bg-white', 'border-red-200', 'text-red-300'],
      },
      {
        color: 'default',
        intent: 'text',
        isDisabled: false,
        class: [
          'text-neutral-950',
          'hover:bg-neutral-100',
          'active:after:inset-shadow-[0_2px_0_0_rgba(0,0,0,0.15)]',
        ],
      },
      {
        intent: 'text',
        isDisabled: true,
        class: ['text-neutral-300'],
      },
      {
        color: 'danger',
        intent: 'text',
        isDisabled: false,
        class: [
          'text-red-600',
          'hover:bg-red-50',
          'active:after:inset-shadow-[0_2px_0_0_rgba(231,0,11,0.15)]',
        ],
      },
    ],
    defaultVariants: {
      color: 'default',
      iconOnly: false,
      intent: 'primary',
      isDisabled: false,
      size: 'md',
    },
  }
)

type ButtonColor = 'default' | 'danger'
type ButtonSize = 'sm' | 'md' | 'lg'
type ButtonVariant = 'primary' | 'default' | 'text'

type ButtonLabelProps =
  | {
      ariaLabel?: string
      children: React.ReactNode
    }
  | {
      ariaLabel: string
      children?: never
    }

type Props = {
  className?: string
  color?: ButtonColor
  icon?: IconKey
  isDisabled?: boolean
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void
  size?: ButtonSize
  variant?: ButtonVariant
} & ButtonLabelProps
const Button = ({
  ariaLabel,
  children,
  className,
  color,
  icon,
  isDisabled,
  onClick,
  size,
  variant,
}: Props) => {
  const classNames = twMerge(
    classes({
      color,
      iconOnly: !!icon && !children,
      intent: variant,
      isDisabled,
      size,
    }),
    className
  )

  const TextComponent = size === 'lg' ? T.BodyLarge : T.Body
  const textClassNames = 'text-center text-inherit font-normal leading-none'

  return (
    <button
      type="button"
      aria-label={ariaLabel}
      className={classNames}
      disabled={isDisabled}
      onClick={onClick}
    >
      {icon && (
        <TextComponent as="span" className={textClassNames}>
          <Icon icon={icon} />
        </TextComponent>
      )}
      {!!children && typeof children === 'string' ? (
        <TextComponent as="span" className={textClassNames}>
          {children}
        </TextComponent>
      ) : (
        children
      )}
    </button>
  )
}

export default memo(Button)
